import React from 'react';
import { BookOpenIcon, CheckCircle2Icon, FileTextIcon, GiftIcon, TrophyIcon, XCircleIcon } from 'lucide-react';
import { bonuses, volumes } from '../data/megapack';
const painPoints = ['Before every session you cannot recall the exact tool order, so you end up improvising.', 'You know how to do wood therapy, but not how to structure a 30, 45 or 60-minute session.', 'You always use the same protocol because you have no others documented.', 'A client asks how many sessions she needs and you cannot answer with confidence.', 'You own the wooden tools, but you do not use them all because you are unsure what each one is for.', 'Your clients improve, but they cannot see it, so they stop coming after two sessions.', 'You do not know how to build a service menu or how to price each treatment.', 'You end up searching "wood therapy abdomen protocol" the night before a session.'];
export function Features() {
  return <div className="w-full bg-white" data-id="element-5" data-mp="2rng8sl741eu">
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-20" data-id="element-6" data-mp="2xtewdo72a8cm">
        <div className="mx-auto max-w-3xl text-center" data-id="element-7" data-mp="2xvrmiomz3p0r">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-600" data-id="element-8" data-mp="2sw59af75a8ry">
            Recognize the problem
          </p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-brand-900 md:text-4xl" data-id="element-9" data-mp="36sa3d2hxjmgz">
            If you are a wellness professional, you are probably dealing with
            some of these problems
          </h2>
        </div>
        <div className="mt-9 grid gap-3 md:grid-cols-2" data-id="element-10" data-mp="3bu7v6l4ebplj">
          {painPoints.map(point => <div key={point} className="flex gap-3 rounded-xl border border-brand-100 bg-cream p-4" data-id="element-11" data-mp="3qj3y63rlicfa">
              <XCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" aria-hidden="true" data-id="element-12" data-mp="3lkawd8lo89uy" />
              <p className="text-sm font-medium leading-relaxed text-gray-700" data-id="element-13" data-mp="3ukh7intwcxqy">
                {point}
              </p>
            </div>)}
        </div>
        <p className="mt-10 text-center text-2xl font-black text-brand-600 md:text-3xl" data-id="element-14" data-mp="2s4oo4uhwlqr4">
          What if all of this were already done for you?
        </p>
      </section>

      <section className="bg-brand-900 px-4 py-12 text-white sm:px-6 md:py-20" data-id="element-15" data-mp="316o7htge56x8">
        <div className="mx-auto max-w-6xl text-center" data-id="element-16" data-mp="35yfxa1rq38mb">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-800 px-4 py-2 text-xs font-bold tracking-wide text-accent" data-id="element-17" data-mp="3600w43l2ldpz">
            <TrophyIcon className="h-4 w-4" data-id="element-18" data-mp="3fvknccoovo8e" /> THE MOST COMPLETE SYSTEM
            AVAILABLE
          </div>
          <h2 className="mt-5 text-3xl font-black md:text-5xl" data-id="element-19" data-mp="3kwi6oslfqobh">
            WOOD THERAPY PRO MEGAPACK™
          </h2>
          <p className="mt-3 text-lg font-semibold text-brand-100 md:text-2xl" data-id="element-20" data-mp="3owijk8svyrgf">
            6 Volumes + 5 Bonuses — From the first contact to a complete
            business system
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-gray-300 md:text-base" data-id="element-21" data-mp="1ujj8mszgalfh">
            More than 300 pages of ready protocols, client forms, scripts and
            professional management material — all done for you.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4" data-id="element-22" data-mp="1d9f8pj6o10z6">
            {[['300+', 'pages', FileTextIcon], ['20+', 'ready protocols', BookOpenIcon], ['8', 'tools mastered', TrophyIcon], ['Forms', 'printable included', CheckCircle2Icon]].map(([value, label, Icon]) => {
            const StatIcon = Icon as typeof FileTextIcon;
            return <div key={label as string} className="rounded-xl bg-brand-800/70 p-4" data-id="element-23" data-mp="1wwcxhuioe183">
                  <StatIcon className="mx-auto h-6 w-6 text-accent" data-id="element-24" data-mp="1s0xqnmj355l6" />
                  <p className="mt-2 font-black" data-id="element-25" data-mp="lju1yp1iwg66">{value}</p>
                  <p className="text-xs text-brand-100" data-id="element-26" data-mp="gj6ci7sy07e2">{label}</p>
                </div>;
          })}
          </div>
          <p className="mt-7 text-sm font-medium leading-loose text-brand-100" data-id="element-27" data-mp="24ekovrf1awzl">
            Fundamentals · Body sculpting · Therapeutic work · Drainage and
            postpartum · Sessions and programs · Business and management
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-20" data-id="element-28" data-mp="1zg9g7k0h6siy">
        <div className="text-center" data-id="element-29" data-mp="2n6vam8hk4eah">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-600" data-id="element-30" data-mp="2n7wyxenl2xa5">
            Your reference library
          </p>
          <h2 className="mt-3 text-3xl font-black text-brand-900 md:text-4xl" data-id="element-31" data-mp="2s3lxpfrs4a5e">
            The 6 Complete Volumes
          </h2>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-32" data-mp="2j48qktr4nxnu">
          {volumes.map((volume, index) => <article key={volume.label} className={`rounded-2xl border p-6 ${index === 2 ? 'border-accent bg-accent/5' : 'border-brand-100 bg-white'}`} data-id="element-33" data-mp="12qih17h8oh6t" data-mpi={index}>
              <p className="text-xs font-black tracking-[0.14em] text-brand-600" data-id="element-34" data-mp="z82wqaxww3c8">
                {volume.label}
                {index === 2 ? ' · THE VOLUME THAT SETS YOU APART' : ''}
              </p>
              <h3 className="mt-2 text-xl font-black text-brand-900" data-id="element-35" data-mp="197a84sl35nwp">
                {volume.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-gray-600" data-id="element-36" data-mp="148gvt3hjc2y4">
                {volume.description}
              </p>
            </article>)}
        </div>
        <div className="mt-7 flex flex-wrap justify-center gap-x-5 gap-y-2 rounded-xl border border-brand-100 bg-cream p-5 text-xs font-semibold text-brand-800" data-id="element-37" data-mp="bk1jwptpa0r0">
          {['Complete protocols ready to use', 'Printable and digital client forms', 'Step-by-step checklists', 'Material you can hand to clients', 'Readable from tablet or phone'].map(item => <span key={item} className="flex items-center gap-1.5" data-id="element-38" data-mp="3oxwhjk1wd1gw">
              <CheckCircle2Icon className="h-4 w-4 text-brand-500" data-id="element-39" data-mp="1stytvxdgfe2z" />
              {item}
            </span>)}
        </div>
      </section>

      <section className="bg-brand-50 px-4 py-12 sm:px-6 md:py-20" data-id="element-40" data-mp="1xrryilz40015">
        <div className="mx-auto max-w-5xl" data-id="element-41" data-mp="22ld00nzwvc53">
          <div className="text-center" data-id="element-42" data-mp="26meyh2m07k60">
            <p className="text-sm font-bold text-brand-600" data-id="element-43" data-mp="26ldfmbyqxtdq">
              Included at no extra cost — Total value: $73 →{' '}
              <span className="text-brand-900" data-id="element-44" data-mp="19y0fejdb9eef">FREE with your MegaPack</span>
            </p>
            <h2 className="mt-3 text-3xl font-black text-brand-900 md:text-4xl" data-id="element-45" data-mp="1evioc9rbvtmf">
              The 5 Strategic Bonuses
            </h2>
          </div>
          <div className="mt-9 space-y-4" data-id="element-46" data-mp="1nwgi7z81ykra">
            {bonuses.map((bonus, index) => <article key={bonus.title} className="flex gap-4 rounded-xl border border-brand-100 bg-white p-4 sm:gap-5 sm:p-5" data-id="element-47" data-mp="2uc9axj2ev6pq" data-mpi={index}>
                <img src={bonus.image} alt={`Cover of the bonus: ${bonus.title}`} className="h-24 w-24 shrink-0 rounded-md border border-brand-100 bg-cream object-contain sm:h-32 sm:w-32" data-id="element-48" data-mp="2zcx1k3mq9ljf" />
                <div className="min-w-0 py-0.5" data-id="element-49" data-mp="2znwl9mcllbge">
                  <div className="flex items-center gap-2" data-id="element-50" data-mp="2vnm4den9p0mr">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15" data-id="element-51" data-mp="2vn4auprzp0ij">
                      <GiftIcon className="h-4 w-4 text-accent" aria-hidden="true" data-id="element-52" data-mp="2qmga3cdyl5b4" />
                    </div>
                    <p className="text-xs font-bold text-brand-600" data-id="element-53" data-mp="2gqvebvzhh19t">
                      BONUS 0{index + 1} ·{' '}
                      <span className="text-gray-400 line-through" data-id="element-54" data-mp="2cqurbmybjhr5">
                        {bonus.value}
                      </span>{' '}
                      · FREE
                    </p>
                  </div>
                  <h3 className="mt-2 text-base font-black text-brand-900 sm:text-lg" data-id="element-55" data-mp="avk94swgfcj4">
                    {bonus.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-600" data-id="element-56" data-mp="y6ozxtrnebj9">
                    {bonus.description}
                  </p>
                </div>
              </article>)}
          </div>
        </div>
      </section>
    </div>;
}